import invariant from 'invariant'

import {getParentElements, getScrollParents, requestAnimationFrameThrottle} from './dom'

export type Dimensions = {
  width: number
  height: number
}

export type Position = {
  x: number
  y: number
}

export type Rectangle = Dimensions & Position

export type Observer<A extends unknown[]> = (...args: A) => () => void

const hasResizeObserver = typeof ResizeObserver !== 'undefined'

/**
 * Holds a singleton ResizeObserver and multiplexes registered resize handlers.
 * Automatically cleans up when the last ResizeObserver is unregistered.
 */
export class ResizeObserverSingleton {
  static instance: ResizeObserverSingleton | null = null

  private observer: ResizeObserver | null = null

  private listeners: Map<Element, ResizeObserverCallback> = new Map()

  constructor() {
    if (ResizeObserverSingleton.instance) {
      const instance = ResizeObserverSingleton.instance

      invariant(!!instance.observer, 'ResizeOberver missing.')
    }

    ResizeObserverSingleton.instance = this

    if (hasResizeObserver) {
      this.observer = new ResizeObserver(entries => {
        entries.forEach(entry => {
          const callback = this.listeners.get(entry.target)

          if (callback) {
            callback([entry], this.observer!)
          }
        })
      })
    }
  }

  destroy() {
    this.observer?.disconnect()
    ResizeObserverSingleton.instance = null
  }

  static observe(element: HTMLElement, callback: ResizeObserverCallback) {
    let instance = ResizeObserverSingleton.instance
    if (!instance) {
      instance = new ResizeObserverSingleton()
    }

    instance.listeners.set(element, callback)
    instance.observer?.observe(element)
  }

  static unobserve(element: HTMLElement) {
    const instance = ResizeObserverSingleton.instance
    if (!instance) {
      return
    }

    instance.listeners.delete(element)
    instance.observer?.unobserve(element)

    if (instance.listeners.size === 0) {
      instance.destroy()
    }
  }
}

export function convertClientRectToDocumentRect(element: HTMLElement, rect: DOMRect): Rectangle {
  return {
    width: rect.width,
    height: rect.height,
    x: rect.x + window.scrollX,
    y: rect.y + window.scrollY,
  }
}

export function observeScrollParents(element: HTMLElement, callback: () => void) {
  const parents = getScrollParents(element)

  parents.forEach(parent => {
    parent.addEventListener('scroll', callback)
  })

  return () => {
    parents.forEach(parent => {
      parent.removeEventListener('scroll', callback)
    })
  }
}

export function observeResizeWithParents(element: HTMLElement, callback: ResizeObserverCallback) {
  const parents = getParentElements(element)

  parents.forEach(parent => {
    ResizeObserverSingleton.observe(parent, callback)
  })

  ResizeObserverSingleton.observe(element, callback)

  return () => {
    parents.forEach(parent => {
      ResizeObserverSingleton.unobserve(parent)
    })

    ResizeObserverSingleton.unobserve(element)
  }
}

/**
 * Tracks the dimensions of an element.
 * Use this instead of observePosition if you are only interested in the dimensions because it is more efficient.
 */
export function observeDimensions(
  element: HTMLElement,
  callback: (dimensions: Dimensions) => void
) {
  const throttledCallback = requestAnimationFrameThrottle(callback)

  ResizeObserverSingleton.observe(element, ([entry]) => {
    throttledCallback({
      width: entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width,
      height: entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height,
    })
  })

  return () => {
    ResizeObserverSingleton.unobserve(element)
  }
}

/**
 * Tracks the position and dimensions of an element relative to the document (not the viewport).
 */
export function observeDocumentRect(element: HTMLElement, callback: (rect: Rectangle) => void) {
  const cleanups: Array<() => void> = []

  const update = () => {
    const rect = convertClientRectToDocumentRect(element, element.getBoundingClientRect())

    callback(rect)
  }

  const throttledUpdate = requestAnimationFrameThrottle(update)

  cleanups.push(observeResizeWithParents(element, throttledUpdate))
  cleanups.push(observeScrollParents(element, throttledUpdate))

  return () => {
    cleanups.forEach(cleanup => cleanup())
  }
}

export function observeIntersection(
  element: HTMLElement,
  callback: (entry: IntersectionObserverEntry) => void,
  options?: IntersectionObserverInit
) {
  const update: IntersectionObserverCallback = entries => callback(entries[0])

  const throttledUpdate = requestAnimationFrameThrottle(update)

  const observer = new IntersectionObserver(throttledUpdate, options)

  observer.observe(element)

  return () => {
    observer.disconnect()
  }
}

export function observeScroll(element: HTMLElement | Window, callback: (event: Event) => void) {
  element.addEventListener('scroll', callback)

  return () => {
    element.removeEventListener('scroll', callback)
  }
}

export function observeWindowResize(callback: () => void) {
  const throttledCallback = requestAnimationFrameThrottle(callback)

  window.addEventListener('resize', throttledCallback)
  window.addEventListener('orientationchange', throttledCallback)

  return () => {
    window.removeEventListener('resize', throttledCallback)
    window.removeEventListener('orientationchange', throttledCallback)
  }
}
